import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  CalendarDays, MapPin, Clock, UtensilsCrossed, DollarSign,
  Coffee, Cookie, Salad, Sun, Moon as MoonIcon, CupSoda
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEAL_TYPES = [
  { value: "Breakfast", icon: Coffee, color: "bg-amber-500/10 text-amber-600" },
  { value: "AM Break", icon: Cookie, color: "bg-orange-500/10 text-orange-600" },
  { value: "Lunch", icon: Salad, color: "bg-green-500/10 text-green-600" },
  { value: "PM Break", icon: CupSoda, color: "bg-blue-500/10 text-blue-600" },
  { value: "Dinner", icon: Sun, color: "bg-purple-500/10 text-purple-600" },
  { value: "All Day Snacks", icon: MoonIcon, color: "bg-rose-500/10 text-rose-600" },
];

const STATUS_COLORS = {
  pending: "bg-amber-500/10 text-amber-600",
  approved: "bg-emerald-500/10 text-emerald-600",
  rejected: "bg-red-500/10 text-red-600",
  complete: "bg-blue-500/10 text-blue-600",
};

export default function PublicEventPage() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPublicEvent = async () => {
      try {
        const res = await axios.get(`${API}/public/event/${shareToken}`);
        setData(res.data);
      } catch {
        setError("This event link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    };
    fetchPublicEvent();
  }, [shareToken]);

  const costBreakdown = useMemo(() => {
    if (!data?.meals) return { byType: {}, grandTotal: 0 };
    const byType = {};
    let grandTotal = 0;
    data.meals.forEach((meal) => {
      const items = meal.menu_items?.split(",").map((s) => s.trim()).filter(Boolean) || [];
      const vendor = data.vendors?.find((v) => v.vendor_id === meal.vendor_id);
      let mealTotal = 0;
      items.forEach((itemName) => {
        const vendorItem = vendor?.menu_items?.find((mi) => mi.name === itemName);
        if (vendorItem?.price) mealTotal += vendorItem.price;
      });
      if (!byType[meal.meal_type]) byType[meal.meal_type] = 0;
      byType[meal.meal_type] += mealTotal;
      grandTotal += mealTotal;
    });
    return { byType, grandTotal };
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="public-event-loading">
        <div className="h-10 w-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="public-event-error">
        <Card className="max-w-md w-full border-border/50">
          <CardContent className="text-center py-12">
            <CalendarDays className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-bold text-foreground">Link Not Found</h2>
            <p className="text-muted-foreground mt-2">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { event, meals, vendors } = data;

  return (
    <div className="min-h-screen bg-background" data-testid="public-event-page">
      <div className="max-w-4xl mx-auto p-6 md:p-8 lg:p-12 space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <Badge className="text-xs bg-primary/10 text-primary">Public Event Schedule</Badge>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground" data-testid="public-event-title">
            {event.title}
          </h1>
          {event.description && (
            <p className="text-muted-foreground max-w-2xl mx-auto">{event.description}</p>
          )}
          <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {event.start_date} — {event.end_date}</span>
            {event.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {event.location}</span>}
            <Badge className={STATUS_COLORS[event.status] || ""}>{event.status}</Badge>
          </div>
        </div>

        {/* Day-by-day Schedule */}
        {event.days?.map((day) => {
          const dayMeals = meals.filter((m) => m.day_number === day.day_number);
          return (
            <Card key={day.day_number} className="border-border/50 shadow-sm" data-testid={`public-day-${day.day_number}`}>
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Day {day.day_number}: {day.day_name} — {day.date}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dayMeals.length > 0 ? (
                  <div className="space-y-3">
                    {dayMeals.map((meal) => {
                      const mealType = MEAL_TYPES.find((m) => m.value === meal.meal_type);
                      const Icon = mealType?.icon || UtensilsCrossed;
                      const vendor = vendors.find((v) => v.vendor_id === meal.vendor_id);
                      return (
                        <div key={meal.meal_id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/30">
                          <div className={`p-2 rounded-lg ${mealType?.color || "bg-muted text-muted-foreground"}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-foreground">{meal.meal_type}</span>
                              {vendor && <Badge variant="secondary" className="text-[10px]">{vendor.name}</Badge>}
                            </div>
                            {meal.menu_items && (
                              <p className="text-xs text-muted-foreground mt-1">{meal.menu_items}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No meals planned for this day</p>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Cost Summary */}
        {costBreakdown.grandTotal > 0 && (
          <Card className="border-border/50 shadow-sm" data-testid="public-cost-summary">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-500" /> Cost Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(costBreakdown.byType).map(([type, amount]) => (
                  <div key={type} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{type}</span>
                    <span className="font-medium text-foreground">${amount.toFixed(2)}</span>
                  </div>
                ))}
                <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Total</span>
                  <span className="text-xl font-bold text-emerald-600">${costBreakdown.grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground/50 py-4">
          IVEM Food Portal — Shared Event Schedule
        </div>
      </div>
    </div>
  );
}
